
import { useState } from "react";
import { Link } from "react-router-dom";
import { Award, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface LeaderboardEntry {
  id: string;
  rank: number;
  name: string;
  score: number;
  date: string;
  avatar?: string;
  category: string;
}

interface LeaderboardItemProps {
  entry: LeaderboardEntry;
  isTop3?: boolean;
}

export function LeaderboardItem({ entry, isTop3 = false }: LeaderboardItemProps) {
  const [isHovering, setIsHovering] = useState(false);

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return "text-yellow-500 border-yellow-500/30 bg-yellow-500/10";
      case 2:
        return "text-gray-300 border-gray-300/30 bg-gray-300/10";
      case 3:
        return "text-amber-600 border-amber-600/30 bg-amber-600/10";
      default:
        return "text-white/70 border-white/10 bg-white/5";
    }
  };

  const getAvatarBorder = (rank: number) => {
    switch (rank) {
      case 1:
        return "border-yellow-500";
      case 2:
        return "border-gray-300";
      case 3:
        return "border-amber-600";
      default:
        return "border-white/20";
    }
  };

  return (
    <Link
      to={`/submission/${entry.id}`}
      className={cn(
        "block group",
        isTop3 ? "mb-3" : "mb-2"
      )}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div
        className={cn(
          "glass-card p-4 rounded-lg flex items-center justify-between transition-all duration-200",
          isHovering ? "bg-card/90 scale-[1.01]" : "bg-card/60"
        )}
      >
        <div className="flex items-center space-x-3">
          <div
            className={cn(
              "flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full border",
              getRankColor(entry.rank),
              isTop3 ? "w-12 h-12" : "w-10 h-10"
            )}
          >
            {entry.rank <= 3 ? (
              <Award className={cn("w-5 h-5", isTop3 ? "w-6 h-6" : "w-5 h-5")} />
            ) : (
              <span className="font-bold">{entry.rank}</span>
            )}
          </div>
          <div className="flex flex-col">
            <span className={cn(
              "font-medium",
              isTop3 ? "text-lg" : "text-base"
            )}>
              {entry.name}
            </span>
            <span className="text-xs text-white/60">{entry.date}</span>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex flex-col items-end">
            <span className="text-sm text-white/60">Score</span>
            <span className={cn(
              "font-bold",
              isTop3 ? "text-xl text-primary" : "text-lg"
            )}>
              {entry.score}
            </span>
          </div>
          <ChevronRight className={cn(
            "w-4 h-4 text-white/50 transition-transform duration-300",
            isHovering ? "transform translate-x-1" : ""
          )} />
        </div>
      </div>
    </Link>
  );
}
